import type { CoasterProject, TrackNode } from '../schema/index.ts';
import { fixedPointToWorld } from './fixed-grid.ts';

/**
 * Authored-grid pieces are durable records. Nodes and sections are only their
 * compiled projection for rendering, validation, and physics.
 */
export function materializeAuthoredProject(project: CoasterProject): CoasterProject {
  if (project.track.geometryKind !== 'authored_grid_v2') return project;
  if (!project.track.pieces.length) return structuredClone(project);

  const priorSections = new Map(project.track.sections.map((section) => [section.id, section]));
  const nodes: TrackNode[] = [];
  type AuthoredTrack = Extract<CoasterProject['track'], { geometryKind: 'authored_grid_v2' }>;
  const sections: AuthoredTrack['sections'][number][] = [];
  const pieceGroups = [];
  const piecesById = new Map(project.track.pieces.map((piece) => [piece.id, piece]));

  for (const component of project.track.authoredTopology.components) {
    const componentPieces = component.pieceIds.map((pieceId) => {
      const piece = piecesById.get(pieceId);
      if (!piece) throw new Error(`Authored component references missing piece ${pieceId}.`);
      return piece;
    });
    let previousNodeId: string | null = null;
    let componentStartNodeId: string | null = null;

    for (const [pieceIndex, piece] of componentPieces.entries()) {
      if (piece.spans.length !== piece.sectionIds.length)
        throw new Error(`Authored piece ${piece.id} has a damaged span/section contract.`);
      const firstSection = priorSections.get(piece.sectionIds[0]!);
      if (!firstSection)
        throw new Error(`Authored piece ${piece.id} is missing its first owned section.`);

      if (pieceIndex === 0) {
        previousNodeId = firstSection.fromNodeId;
        componentStartNodeId = previousNodeId;
        const firstSpan = piece.spans[0]!;
        nodes.push({
          id: previousNodeId,
          position: fixedPointToWorld(piece.entryPose),
          bankDegrees: firstSpan.bankStartDegrees,
          rollWindingDegrees: firstSpan.windingStartDegrees,
        });
      }
      if (!previousNodeId || !componentStartNodeId)
        throw new Error(`Authored piece ${piece.id} has no durable entry node.`);

      for (const [spanIndex, span] of piece.spans.entries()) {
        const sectionId = piece.sectionIds[spanIndex]!;
        const prior = priorSections.get(sectionId);
        if (!prior)
          throw new Error(`Authored piece ${piece.id} is missing owned section ${sectionId}.`);
        const isClosingSpan =
          component.closed &&
          pieceIndex === componentPieces.length - 1 &&
          spanIndex === piece.spans.length - 1;
        const toNodeId = isClosingSpan ? componentStartNodeId : prior.toNodeId;

        sections.push({
          id: sectionId,
          fromNodeId: previousNodeId,
          toNodeId,
          behavior: structuredClone(piece.behavior),
          geometry: {
            kind: 'cubic_bezier',
            control1: fixedPointToWorld(span.control1),
            control2: fixedPointToWorld(span.control2),
          },
        });
        if (!isClosingSpan)
          nodes.push({
            id: toNodeId,
            position: fixedPointToWorld(span.end),
            bankDegrees: span.bankEndDegrees,
            rollWindingDegrees: span.windingEndDegrees,
          });
        previousNodeId = toNodeId;
      }
    }
  }

  for (const piece of project.track.pieces)
    pieceGroups.push({
      id: piece.id,
      definitionId: 'custom' as const,
      sectionIds: [...piece.sectionIds],
      parameters: null,
    });

  return {
    ...structuredClone(project),
    track: { ...structuredClone(project.track), nodes, sections },
    editor: { ...structuredClone(project.editor), pieceGroups },
  };
}

export function authoredPieceProjectionMatches(project: CoasterProject): boolean {
  if (project.track.geometryKind !== 'authored_grid_v2') return true;
  try {
    const materialized = materializeAuthoredProject(project);
    return (
      JSON.stringify(materialized.track.nodes) === JSON.stringify(project.track.nodes) &&
      JSON.stringify(materialized.track.sections) === JSON.stringify(project.track.sections) &&
      JSON.stringify(materialized.editor.pieceGroups) === JSON.stringify(project.editor.pieceGroups)
    );
  } catch {
    return false;
  }
}

/** Produce independently compilable component projects for construction-only rendering. */
export function authoredConstructionComponentProjects(
  project: CoasterProject,
): readonly CoasterProject[] {
  if (project.track.geometryKind !== 'authored_grid_v2') return [project];
  const materialized = materializeAuthoredProject(project);
  if (materialized.track.geometryKind !== 'authored_grid_v2') return [materialized];
  const track = materialized.track;
  return track.authoredTopology.components.map((component) => {
    const pieceIds = new Set(component.pieceIds);
    const pieces = track.pieces.filter((piece) => pieceIds.has(piece.id));
    const sectionIds = new Set(pieces.flatMap((piece) => piece.sectionIds));
    const sections = track.sections.filter((section) => sectionIds.has(section.id));
    const nodeIds = new Set<string>();
    sections.forEach((section) => {
      nodeIds.add(section.fromNodeId);
      nodeIds.add(section.toNodeId);
    });
    return {
      ...structuredClone(materialized),
      track: {
        ...structuredClone(track),
        closed: component.closed,
        pieces: structuredClone(pieces),
        sections: structuredClone(sections),
        nodes: track.nodes.filter((node) => nodeIds.has(node.id)),
        authoredTopology: { version: 1, components: [structuredClone(component)] },
      },
      supports: {
        ...structuredClone(materialized.supports),
        suppressedSectionIds: materialized.supports.suppressedSectionIds.filter((sectionId) =>
          sectionIds.has(sectionId),
        ),
        manual: materialized.supports.manual.filter((support) => sectionIds.has(support.sectionId)),
      },
      highlights: materialized.highlights.filter((highlight) =>
        sectionIds.has(highlight.sectionId),
      ),
      editor: {
        ...structuredClone(materialized.editor),
        pieceGroups: materialized.editor.pieceGroups.filter((group) => pieceIds.has(group.id)),
      },
    };
  });
}
